import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { authClient } from "../lib/authClient";
import { useLanguage } from "./LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const USE_BETTER_AUTH = process.env.REACT_APP_USE_BETTER_AUTH === "true";
const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blockedInfo, setBlockedInfo] = useState(null);
  const esRef = useRef(null);
  const { setLang } = useLanguage();

  const checkAuth = useCallback(async () => {
    try {
      const res = await fetch(`${API}/session/me`, { credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 403) {
          const blockedPayload = {
            blocked: true,
            reason: data.detail || "Account is blocked",
          };
          setBlockedInfo(blockedPayload);
          setUser(null);
          return blockedPayload;
        }
        if (res.status === 401) {
          setUser(null);
          setBlockedInfo(null);
        }
        return null;
      }
      setBlockedInfo(null);
      setUser(data);
      const localLang = localStorage.getItem("ifollowers_lang") || "he";
      if (data.preferred_lang && ["en", "ru", "he"].includes(data.preferred_lang)) {
        // DB is source of truth — apply it to the UI if different
        if (data.preferred_lang !== localLang) {
          setLang(data.preferred_lang);
        }
      } else {
        // No lang in DB yet — save browser-detected lang to backend
        fetch(`${API}/user/language`, {
          method: "PUT", credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ lang: localLang }),
        }).catch(() => {});
      }
      return data;
    } catch {
      return null;
    } finally {
      setLoading(false);
    }
  }, [setLang]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // ── Realtime SSE stream ──────────────────────────────────────────────────
  useEffect(() => {
    if (!user?.user_id) {
      if (esRef.current) { esRef.current.close(); esRef.current = null; }
      return;
    }

    const connect = () => {
      const es = new EventSource(`${API}/user/stream`, { withCredentials: true });
      esRef.current = es;

      es.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          setUser(prev => {
            if (!prev) return prev;
            const changed =
              prev.ipoints !== data.ipoints ||
              prev.xp !== data.xp;
            if (!changed) return prev;
            return { ...prev, ipoints: data.ipoints, xp: data.xp };
          });
          // Notify other components (messages badge, dashboard, etc.)
          window.dispatchEvent(new CustomEvent("ifollowers:live", { detail: data }));
        } catch {}
      };

      es.onerror = () => {
        es.close();
        esRef.current = null;
        // Reconnect after 6 seconds
        setTimeout(() => {
          if (esRef.current === null) connect();
        }, 6000);
      };
    };

    connect();
    return () => { if (esRef.current) { esRef.current.close(); esRef.current = null; } };
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const login = () => {
    if (blockedInfo?.blocked) {
      window.location.assign("/blocked");
      return;
    }
    const next = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    window.location.assign(`/login?next=${encodeURIComponent(next || "/dashboard")}`);
  };

  const logout = async () => {
    try {
      if (USE_BETTER_AUTH) {
        await authClient.signOut();
      }
    } catch {}
    try {
      await fetch(`${API}/session/logout`, { method: "POST", credentials: "include" });
    } catch {}
    if (window.google?.accounts?.id?.disableAutoSelect) {
      window.google.accounts.id.disableAutoSelect();
    }
    setUser(null);
    setBlockedInfo(null);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, loading, blockedInfo, login, logout, checkAuth, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be inside AuthProvider");
  return ctx;
}
